import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/custom_variables';

const DeleteCustomVariableVariablesSchema = z.object({
  id: z.number(),
});

export type DeleteCustomVariableVariables = z.infer<typeof DeleteCustomVariableVariablesSchema>;

export async function deleteCustomVariableFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DeleteCustomVariableVariables,
) {
  const parsed = DeleteCustomVariableVariablesSchema.parse(variables);
  const instance = await axiosInstance;
  return instance.delete(`${URL}/${parsed.id}`);
}

export function useDeleteCustomVariable() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DeleteCustomVariableVariables>({
    mutationFn: variables => deleteCustomVariableFn(axiosInstance, variables),
    meta: { suppressSuccessNotification: true },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['customVariables'] });
      client.invalidateQueries({ queryKey: ['company-configuration'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DeleteCustomVariableVariables,
    'deleteCustomVariable'
  >('deleteCustomVariable', mutation);
}
